import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { leadSchema, type LeadFormData } from '@/lib/validators';
import { useLead, useLeads } from '@/composables/crm/useLeads';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormCurrencyInput } from '@/components/forms/FormCurrencyInput';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ArrowLeft } from 'lucide-react';

const sourceOptions = [
  { label: 'Website', value: 'WEBSITE' },
  { label: 'Referral', value: 'REFERRAL' },
  { label: 'Social Media', value: 'SOCIAL_MEDIA' },
  { label: 'Cold Call', value: 'COLD_CALL' },
  { label: 'Email Campaign', value: 'EMAIL_CAMPAIGN' },
  { label: 'Trade Show', value: 'TRADE_SHOW' },
  { label: 'Partner', value: 'PARTNER' },
  { label: 'Other', value: 'OTHER' },
];

const statusOptions = [
  { label: 'New', value: 'NEW' },
  { label: 'Contacted', value: 'CONTACTED' },
  { label: 'Qualified', value: 'QUALIFIED' },
  { label: 'Unqualified', value: 'UNQUALIFIED' },
];

export function LeadCreatePage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { createLead, updateLead } = useLeads();
  const { lead, isLoading } = useLead(id || '');

  const form = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
    defaultValues: { firstName: '', lastName: '', email: '', phone: '', company: '', jobTitle: '', source: 'OTHER', status: 'NEW', score: 0, description: '' },
  });

  useEffect(() => {
    if (lead && isEdit) {
      form.reset({
        firstName: lead.firstName || '',
        lastName: lead.lastName || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        jobTitle: lead.jobTitle || '',
        source: (lead.source as LeadFormData['source']) || 'OTHER',
        status: (lead.status as LeadFormData['status']) || 'NEW',
        score: lead.score ?? 0,
        estimatedValue: lead.estimatedValue ?? undefined,
        website: lead.website || '',
        description: lead.description || '',
        address: lead.address || '',
        city: lead.city || '',
        state: lead.state || '',
        country: lead.country || '',
        postalCode: lead.postalCode || '',
      });
    }
  }, [lead, isEdit, form]);

  const onSubmit = (data: LeadFormData) => {
    if (isEdit) {
      updateLead.mutate({ id, data }, { onSuccess: () => navigate(`/leads/${id}`) });
    } else {
      createLead.mutate(data, { onSuccess: () => navigate('/leads') });
    }
  };

  if (isEdit && isLoading) return <LoadingSpinner />;

  const isPending = isEdit ? updateLead.isPending : createLead.isPending;

  return (
    <PageContainer backTo
      title={isEdit ? 'Edit Lead' : 'Create Lead'}
      description={isEdit ? 'Update lead information' : 'Add a new lead to your pipeline'}
      actions={
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      }
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Basic Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="firstName" label="First Name" required />
                <FormField form={form} name="lastName" label="Last Name" required />
              </div>
              <FormField form={form} name="email" label="Email" type="email" />
              <FormField form={form} name="phone" label="Phone" />
              <FormField form={form} name="company" label="Company" />
              <FormField form={form} name="jobTitle" label="Job Title" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Lead Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <FormSelect form={form} name="source" label="Source" options={sourceOptions} />
              <FormSelect form={form} name="status" label="Status" options={statusOptions} />
              <FormField form={form} name="score" label="Score (0-100)" type="number" />
              <FormCurrencyInput form={form} name="estimatedValue" label="Estimated Value" />
              <FormField form={form} name="website" label="Website" />
              <FormTextarea form={form} name="description" label="Description" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Address</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <FormField form={form} name="address" label="Address" />
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="city" label="City" />
                <FormField form={form} name="state" label="State" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="country" label="Country" />
                <FormField form={form} name="postalCode" label="Postal Code" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Lead')}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
